import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  GraduationCap,
  Users,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";
import BreadcrumbSchema from "../../components/public/BreadcrumbSchema";

/**
 * Public — Training & Workshops service page.
 * Route: /services/training-workshops
 *
 * IMAGE ASSETS
 * Local (not hotlinked). Pexels License. See report for provenance.
 */
const IMAGES = {
  hero: {
    src: "/images/training-workshops/hero.jpg",
    alt: "A facilitator standing at a workplace table, guiding a small group of employees through an engaged, practical discussion.",
  },
  roundtable: {
    src: "/images/training-workshops/roundtable.jpg",
    alt: "A small group of professionals seated around a warm, casual roundtable in an attentive, collaborative discussion.",
  },
};

const TOPICS = [
  "Employee stress and resilience",
  "Understanding and responding to burnout",
  "Grief and loss in the workplace",
  "Supporting employees through serious illness or family crisis",
  "Caregiver stress",
  "Compassionate communication",
  "Workplace crisis and difficult-event support",
  "Leadership support and manager awareness",
  "Supporting teams after loss or difficult change",
  "Boundaries when helping distressed employees",
  "Recognizing when additional professional support may be needed",
  "Creating a workplace where employees feel seen, heard, and respected",
];

const AUDIENCES = [
  "Employees",
  "Managers and supervisors",
  "HR and People leaders",
  "Executives and leadership teams",
  "Frontline or shift-based teams",
  "Caregiving professionals",
  "Mixed employee groups",
  "Other organizational audiences appropriate to the requested topic",
];

const WORKSHOP_ELEMENTS = [
  "Practical education",
  "Guided discussion",
  "Workplace scenarios",
  "Reflection questions",
  "Communication tools",
  "Stress-management or coping strategies",
  "Manager guidance",
  "Resource awareness",
  "Opportunity for questions",
  "Appropriate next-step or referral information",
];

const NOT_REPLACING = [
  "Human Resources",
  "Employee Assistance Programs",
  "Licensed mental-health professionals",
  "Medical providers",
  "Legal counsel",
  "Safety or compliance professionals",
  "Emergency responders",
];

const DELIVERY_FORMATS = [
  "On-site",
  "Remotely",
  "As part of a leadership meeting",
  "As an employee workshop",
  "As a focused team session",
  "In conjunction with an existing CCCHAPS care arrangement",
];

function SectionHeading({ eyebrow, children }) {
  return (
    <div className="mb-6">
      {eyebrow && (
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
          {eyebrow}
        </p>
      )}
      <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
        {children}
      </h2>
    </div>
  );
}

export default function TrainingWorkshopsPage() {
  usePageMeta({
    title: "Training & Workshops | California Corporate Chaplains",
    description:
      "CCCHAPS training and workshops — practical, non-clinical organizational education and workforce support for employees, managers, HR, and leadership. Not religious programming.",
  });

  return (
    <article className="bg-white" data-testid="service-detail-training-workshops">
      <BreadcrumbSchema
        id="training-workshops"
        items={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: "Training & Workshops", path: "/services/training-workshops" },
        ]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-10 md:pb-16">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-8"
            data-testid="tw-back-to-services"
          >
            <ArrowLeft className="w-4 h-4" /> All services
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
                Service
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
                Training &amp; Workshops
              </h1>
              <p className="mt-5 text-xl sm:text-2xl text-slate-700 font-heading leading-snug">
                Practical Education for the Human Side of Work
              </p>

              <div className="mt-8 space-y-5 text-base sm:text-lg text-slate-700 leading-relaxed">
                <p>
                  Workplaces regularly encounter issues that policies alone
                  cannot fully address — grief, caregiving pressure, burnout,
                  serious illness, conflict, difficult transitions, and the
                  emotional impact of crisis.
                </p>
                <p>
                  California Corporate Chaplains provides practical
                  organizational education and workforce-support workshops
                  designed to help employees, managers, and leaders better
                  understand and respond to these human realities.
                </p>
              </div>

              <div className="mt-8 rounded-2xl border border-blue-100 bg-[#F0F4F8] px-6 py-5">
                <p className="text-base sm:text-lg text-slate-800 leading-relaxed">
                  CCCHAPS training and workshops are organizational education
                  and workforce support — not religious programming.
                </p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  Sessions are designed for mixed workforces and are
                  respectful of belief and non-belief.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/schedule?type=training_workshop"
                  data-testid="tw-hero-cta-primary"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
                >
                  Request a Training or Workshop Consultation{" "}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div
                className="aspect-[5/6] rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100"
                data-testid="tw-image-slot-hero"
              >
                <img
                  src={IMAGES.hero.src}
                  alt={IMAGES.hero.alt}
                  loading="eager"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Topics */}
        <section className="mt-4 md:mt-6">
          <SectionHeading eyebrow="Topics">
            Topics We Can Bring to Your Organization
          </SectionHeading>
          <p className="text-base text-slate-700 leading-relaxed mb-6">
            CCCHAPS workshops can address topics such as:
          </p>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="tw-topics"
          >
            {TOPICS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Check
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Built Around Your Workplace */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Customization">
            Built Around Your Workplace
          </SectionHeading>
          <div className="grid md:grid-cols-5 gap-8 items-start">
            <div className="md:col-span-3 space-y-5 text-base text-slate-700 leading-relaxed">
              <p>
                The same workshop should not look identical in every
                organization.
              </p>
              <p>
                A hospice team repeatedly exposed to death and loss may need
                a different emphasis than a construction crew, restaurant
                team, senior-living community, healthcare organization,
                office environment, or leadership group.
              </p>
              <p>
                CCCHAPS adjusts the examples, emphasis, discussion, and
                practical application to the organization's workforce,
                industry, and reason for requesting the session.
              </p>
              <p>
                A workshop may be requested in response to a specific
                concern, as part of leadership or employee development,
                after a difficult workplace event, or simply as proactive
                education around an issue affecting employees.
              </p>
            </div>
            <div className="md:col-span-2">
              <div
                className="aspect-[4/5] rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100"
                data-testid="tw-image-slot-roundtable"
              >
                <img
                  src={IMAGES.roundtable.src}
                  alt={IMAGES.roundtable.alt}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* For Employees, Leaders, or Both */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Audiences">
            For Employees, Leaders, or Both
          </SectionHeading>
          <p className="text-base text-slate-700 leading-relaxed mb-6">
            Sessions may be designed for:
          </p>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="tw-audiences"
          >
            {AUDIENCES.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Users
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={1.75}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 space-y-4 text-base text-slate-700 leading-relaxed">
            <p>
              The content and facilitation style should reflect who is in the
              room.
            </p>
            <p>
              A manager-focused session may emphasize how to respond
              appropriately when an employee is struggling.
            </p>
            <p>
              An employee session may focus more heavily on self-awareness,
              practical coping tools, communication, available resources, and
              knowing when to seek additional support.
            </p>
          </div>
        </section>

        {/* What a Workshop Looks Like */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Format">
            What a CCCHAPS Workshop Looks Like
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed mb-6">
            <p>
              CCCHAPS workshops are designed to be practical, accessible,
              and relevant to real workplace situations.
            </p>
            <p>
              Depending on the topic and format, a session may include:
            </p>
          </div>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="tw-elements"
          >
            {WORKSHOP_ELEMENTS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <GraduationCap
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={1.75}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 space-y-3 text-base text-slate-700 leading-relaxed">
            <p>
              The purpose is not to lecture employees about what they should
              feel.
            </p>
            <p>
              The purpose is to give people language, understanding, and
              practical tools they can use when difficult life or workplace
              situations arise.
            </p>
          </div>
        </section>

        {/* Stand-alone or complementary */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Stand-alone or complementary">
            Training That Can Stand Alone — or Support Ongoing Chaplaincy
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              Organizations do not necessarily need an ongoing workplace
              chaplaincy program in order to request a CCCHAPS workshop.
            </p>
            <p>
              Training and workshops may be provided as a stand-alone
              organizational service where appropriate.
            </p>
            <p>
              For organizations already using CCCHAPS workplace chaplaincy,
              workshops can also complement ongoing chaplain presence by
              providing broader education around concerns employees and
              leaders commonly encounter.
            </p>
            <p>
              This allows an organization to combine individual confidential
              support with workforce-wide education when that combination
              makes sense.
            </p>
          </div>
        </section>

        {/* Boundaries */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Boundaries">
            Clear Professional Boundaries
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed mb-6">
            <p>CCCHAPS workshops are educational and non-clinical.</p>
            <p>
              They do not provide psychotherapy, diagnose mental-health
              conditions, practice medicine, provide legal advice, replace
              required compliance training, or substitute for emergency
              services.
            </p>
            <p>Workshops also do not replace:</p>
          </div>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="tw-not-replacing"
          >
            {NOT_REPLACING.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Check
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-base text-slate-700 leading-relaxed">
            When a workshop topic involves emotional distress, grief, crisis,
            or other sensitive concerns, information should remain within
            appropriate professional chaplain scope and should encourage
            connection with qualified resources when needed.
          </p>
        </section>

        {/* Delivery */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Delivery">
            Designed to Fit the Organization
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed mb-6">
            <p>
              Training may be structured around the needs of the
              organization, audience, topic, and operating environment.
            </p>
            <p>
              Depending on the engagement and current CCCHAPS capabilities,
              sessions may be delivered:
            </p>
          </div>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="tw-delivery"
          >
            {DELIVERY_FORMATS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Check
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Closing */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Prepared for human moments">
            Better Prepared for the Human Moments at Work
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              Managers and employees do not need to become counselors in
              order to respond to one another with greater awareness and
              care.
            </p>
            <p>
              Good workplace education can help people understand what they
              are seeing, communicate more thoughtfully, respect professional
              boundaries, and recognize when another level of support may be
              appropriate.
            </p>
            <p>
              CCCHAPS workshops are designed to help organizations prepare
              for those human moments before they become the only thing
              anyone knows how to talk about.
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section
          className="mt-14 rounded-2xl bg-[#F0F4F8] border border-blue-100 p-6 md:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          data-testid="tw-final-cta"
        >
          <div>
            <h3 className="font-heading text-xl font-medium text-slate-900">
              Ready to Talk?
            </h3>
            <p className="text-sm text-slate-600 mt-1 max-w-xl">
              Whether your organization needs a focused workshop, leadership
              session, employee education, or training that complements an
              ongoing CCCHAPS care arrangement, we can discuss the topic,
              audience, and format that would best fit your workplace.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/schedule?type=training_workshop"
              data-testid="tw-final-cta-schedule"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] whitespace-nowrap"
            >
              Request a Training or Workshop Consultation{" "}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </article>
  );
}
